// Pure billing logic for key deposits (Pfand): a lent key with a deposit product
// becomes a one-off invoice position so the deposit lands on the booking's Beleg
// (it otherwise survives only as flags on the lending row). Side-effect free (no
// facade / HTTP / Angular) → trivially unit testable, like booking-v2-relation-billing.

import { BookingV2KeyAssignment } from '../dtos/booking-v2.dtos';
// Type-only (erased at runtime) → keeps this module free of the payload mapper's
// Angular-tainted import graph while reusing its position shape.
import type { BookingV2PositionDto } from '../services/booking-v2-payload.mapper';

// A key lending is persisted only with a real-person borrower and/or a boat (a
// company-only borrower has no column in the person-key table — see mapKeys).
// Kept here as the single source of truth so the lending filter and the deposit
// billing can't drift: a Pfand is billed only for a key that is actually lent.
export function keyHasPersistedBorrower(k: BookingV2KeyAssignment): boolean {
    return (k.personId != null && k.personKind === 'person') || k.boatId != null;
}

// The booking's deposit positions: one quantity-1 line per PERSISTED key that
// carries a deposit product + amount. The deposit product id ties the line back to
// its Rechnungsversand row for the Beleg transfer. Regenerated on edit (delete the
// baseline + reinsert), like relation positions.
//
// KNOWN LIMIT (shared with mapTenantRelationPositions): a deposit position already
// settled on an invoice is excluded from the baseline (load mapper drops paid ones)
// but is still regenerated here — editing a booking after its Pfand was invoiced
// would duplicate it. Acceptable until a backend discriminator links position↔key.
export function mapKeyDepositPositions(
    keys: readonly BookingV2KeyAssignment[],
    isEdit: boolean,
    baselineDepositPositionIds: readonly number[],
): BookingV2PositionDto[] {
    const result: BookingV2PositionDto[] = keys
        .filter(k => keyHasPersistedBorrower(k)
            && k.depositProductId != null && k.depositAmount != null && k.depositAmount > 0)
        .map(k => ({
            product: { id: k.depositProductId },
            unit: { id: k.depositUnitId ?? null },
            taxRate: { id: k.depositTaxRateId ?? null },
            quantity: 1,
            price: k.depositAmount as number,
            title: k.depositProductName ?? k.keyName,
            discountPercentage: null,
            vestingPeriodFrom: null,
            vestingPeriodUntil: null,
        }));

    if (isEdit) {
        for (const id of baselineDepositPositionIds) {
            result.push({ id, deleted: true } as unknown as BookingV2PositionDto);
        }
    }

    return result;
}
